// AI generated
import { Close, Warning } from '@carbon/icons-react'
import Loading from 'app/_components/Loading'
import type { Editor } from 'slate'
import { useSlateStatic } from 'slate-react'
import { twMerge } from 'tailwind-merge'
import type { UploadEditor } from '.'
import { fileChipClassName } from './File'
import { getClampedAspectRatio } from './Image'
import { type PendingUpload, removePendingUpload } from './pendingUploads'

const labels = {
  image: 'Bild wird hochgeladen…',
  video: 'Video wird hochgeladen…',
  file: 'Datei wird hochgeladen…',
  'file-block': 'Datei wird hochgeladen…',
} satisfies Record<PendingUpload['kind'], string>

const mediaPlaceholderClassName =
  'bg-gray-20 relative mb-4 flex aspect-video w-full flex-col justify-center gap-2 p-4 text-sm'

const variants = {
  file: twMerge(fileChipClassName, 'max-w-full align-middle'),
  'file-block': 'button button-secondary mt-4 flex w-full cursor-default gap-2',
  image: mediaPlaceholderClassName,
  video: mediaPlaceholderClassName,
} satisfies Record<PendingUpload['kind'], string>

// spans only, since the placeholder is rendered inside a paragraph
export default function UploadPlaceholder({
  upload,
}: {
  upload: PendingUpload
}) {
  const editor = useSlateStatic() as Editor & UploadEditor
  const isMedia = upload.kind === 'image' || upload.kind === 'video'

  return (
    <span
      contentEditable={false}
      role={upload.error ? 'alert' : 'status'}
      className={twMerge(
        'text-text-secondary items-center select-none',
        variants[upload.kind],
        upload.error && 'bg-support-error/10 text-support-error',
      )}
      style={
        upload.dimensions && {
          aspectRatio: getClampedAspectRatio(
            upload.dimensions.width,
            upload.dimensions.height,
          ),
        }
      }
    >
      {upload.error ? (
        <Warning
          className={twMerge('shrink-0', isMedia ? 'size-6' : 'size-4')}
          aria-hidden="true"
        />
      ) : (
        <Loading as="span" size={isMedia ? 'md' : 'sm'} aria-hidden />
      )}
      {!upload.error && (
        <span className={twMerge(!isMedia && 'sr-only')}>
          {labels[upload.kind]}
        </span>
      )}
      <span
        className={twMerge(
          'max-w-full truncate font-normal',
          isMedia && 'text-xs',
        )}
      >
        {upload.title}
      </span>
      {upload.error && (
        <>
          <span className="shrink-0">{upload.error}</span>
          <button
            type="button"
            title="Hinweis schließen"
            aria-label="Hinweis schließen"
            className={twMerge(
              'shrink-0 cursor-pointer rounded-full p-0.5 hover:bg-support-error/10',
              isMedia && 'absolute top-2 right-2',
            )}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => removePendingUpload(editor, upload.id)}
          >
            <Close className="size-4" aria-hidden="true" />
          </button>
        </>
      )}
    </span>
  )
}
